/**
 * The menus' sounds: the tactile set of real recorded switches and keys Theo picked for
 * every ACT game (10 Oct 2026: "make these for all the games for now"; CC0, credits in
 * public/audio/ui/CREDITS.md). The files are act-babylon's assets/audio/ui (3d65cdf,
 * levelled per sound), played with client-core's gains, so every game's menus sound
 * alike. Menus only: races keep just their music. Played through Web Audio at the GAME
 * AUDIO volume, and loaded on the first press (browsers start audio only after one).
 */
export type UiSound =
  | "hover"
  | "click"
  | "confirm"
  | "back"
  | "open"
  | "close"
  | "tick"
  | "toggle-on"
  | "toggle-off"
  | "error"
  | "notify";

/**
 * Each sound: its files (where there are two, they take turns), its gain, how much its
 * pitch wobbles, and the least gap between two of it (ms). As client-core's audioManifest
 * (act-babylon 3d65cdf, DR-0097): ui.hover, ui.click, ui.confirm, ui.cancel (back) …
 */
const SOUNDS: Record<UiSound, { files: string[]; gain: number; pitch?: number; gap?: number }> = {
  hover: { files: ["hover-1", "hover-2"], gain: 0.25, pitch: 0.04, gap: 45 },
  click: { files: ["click-1", "click-2"], gain: 0.55, pitch: 0.03, gap: 30 },
  confirm: { files: ["confirm"], gain: 0.5 },
  back: { files: ["back-1", "back-2"], gain: 0.45 },
  open: { files: ["open"], gain: 0.42, gap: 80 },
  close: { files: ["close"], gain: 0.36, gap: 80 },
  tick: { files: ["tick"], gain: 0.22, pitch: 0.05, gap: 45 },
  "toggle-on": { files: ["toggle-on"], gain: 0.4 },
  "toggle-off": { files: ["toggle-off"], gain: 0.4 },
  error: { files: ["error"], gain: 0.5 },
  notify: { files: ["notify"], gain: 0.73, gap: 400 },
};

export class UiSounds {
  private ctx: AudioContext | null = null;
  private loading: Promise<void> | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly turn = new Map<UiSound, number>();
  private readonly lastAt = new Map<UiSound, number>();

  /** `volume`: the GAME AUDIO setting, 0..1. */
  constructor(private readonly volume: () => number) {}

  /** On a press: start the audio and fetch the sounds (once). */
  unlock(): void {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
      } catch {
        return; // no Web Audio: the menus stay silent
      }
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    this.loading ??= this.load();
  }

  play(name: UiSound): void {
    const ctx = this.ctx;
    const volume = this.volume();
    if (!ctx || volume <= 0) return;
    const sound = SOUNDS[name];
    const now = performance.now();
    if (sound.gap && now - (this.lastAt.get(name) ?? -Infinity) < sound.gap) return;
    this.lastAt.set(name, now);
    const i = this.turn.get(name) ?? 0;
    this.turn.set(name, (i + 1) % sound.files.length);
    const buffer = this.buffers.get(sound.files[i]!);
    if (!buffer) return;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    if (sound.pitch) source.playbackRate.value = 1 + (Math.random() * 2 - 1) * sound.pitch;
    const gain = ctx.createGain();
    gain.gain.value = volume * sound.gain;
    source.connect(gain).connect(ctx.destination);
    source.start();
  }

  private async load(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    const names = [...new Set(Object.values(SOUNDS).flatMap((s) => s.files))];
    await Promise.all(
      names.map(async (name) => {
        try {
          const res = await fetch(`./audio/ui/${name}.mp3`);
          this.buffers.set(name, await ctx.decodeAudioData(await res.arrayBuffer()));
        } catch {
          /* a sound that won't load just stays silent */
        }
      }),
    );
  }
}

/** Which sound a button makes when pressed, from what it is (a data-sfx attribute wins). */
function soundOf(el: Element): UiSound | null {
  const named = el.getAttribute("data-sfx");
  if (named) return named === "none" ? null : (named as UiSound);
  if (el.matches(".vd-mus-tog, .vd-gyro")) return null; // toggles sound their new state themselves
  if (el.matches(".back-act, .back-btn, .menu-btn, .panel-close, .vd-pf-mini.cancel")) return "back";
  if (el.matches(".select-act, .start-btn, .retry-btn, .start-local, .vd-pf-mini.save")) return "confirm";
  return "click";
}

/**
 * Every button under `root` sounds when pressed, and when a mouse moves onto it; the
 * first press anywhere starts the audio.
 */
export function wireUiSounds(root: HTMLElement, ui: UiSounds): void {
  const unlock = () => ui.unlock();
  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("keydown", unlock, true);
  let hovered: Element | null = null;
  root.addEventListener("pointerover", (e) => {
    if (e.pointerType !== "mouse") return;
    const el = (e.target as Element | null)?.closest("button") ?? null;
    if (el && el !== hovered && !(el as HTMLButtonElement).disabled) ui.play("hover");
    hovered = el;
  });
  root.addEventListener("click", (e) => {
    const el = (e.target as Element | null)?.closest("button, [data-sfx]");
    if (!el || (el as HTMLButtonElement).disabled) return;
    const sound = soundOf(el);
    if (sound) ui.play(sound);
  });
}
